import { Typography } from "antd";

import { RVIcon, RVIconSizeOptions } from "../../../../atoms/RVIcon/RVIcon";
import { RVColumn } from "../../../../layouts/RVFlexbox/RVFlexbox";

import styles from "./RVHelpPopupWelcome.module.scss";

const DEFAULT_HEADER = "Regal Helper";
const DEFAULT_SUBHEADER = "Hello - how can I assist you?";

export type RVHelpPopupWelcomeProps = {
    /**
     * Icon rendered inside the circular badge.
     */
    icon: string;
    /**
     * Main heading displayed below the icon badge.
     * @default "Regal Helper"
     */
    header?: string;
    /**
     * Supporting text shown beneath the heading.
     * @default "Hello - how can I assist you?"
     */
    subHeader?: string;
};

/**
 * Branded welcome section for use at the top of an `RVHelpPopup`.
 *
 * Renders a circular icon badge, a heading, and a sub-heading, separated from
 * the popup body by a bottom border. All three visuals follow the RV design-
 * system colour tokens — no local overrides needed in consuming components.
 *
 * @example
 * <RVHelpPopup ...>
 *     <RVHelpPopupWelcome />
 *     <RVColumn gap={2} padding={2}>
 *         <RVHelpPopupPresetButton label="..." onClick={...} />
 *         <RVHelpPopupTextareaForm onSubmit={...} />
 *     </RVColumn>
 * </RVHelpPopup>
 */
export function RVHelpPopupWelcome({
    icon,
    header = DEFAULT_HEADER,
    subHeader = DEFAULT_SUBHEADER,
}: RVHelpPopupWelcomeProps): JSX.Element {
    return (
        <RVColumn className={styles.welcomeSection} alignItems="center" gap={3}>
            <span className={styles.iconBadge}>
                <RVIcon size={RVIconSizeOptions.MEDIUM} icon={icon} className={styles.copilotIcon} />
            </span>
            <Typography.Title level={3} className={styles.header}>
                {header}
            </Typography.Title>
            <Typography.Text className={styles.subHeader}>{subHeader}</Typography.Text>
        </RVColumn>
    );
}
